import React, { useState, useEffect } from "react";
import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { sendCustomGaEvent } from "@/scripts/ga";
import { BlueEditPencil, WhiteSpinner } from "./Icons";
import {
  setCourseAcademicDetails,
  setCourseEnglishProficiencyDetails,
} from "@/store/feature/courses/courseSlice";
import { setApplicationRequirementsOpen } from "@/store/feature/common/commonSlice";
import { Course, CourseItem } from "@/store/types/courseTypes";
import { useAuth } from "@/hook/useAuth";
import EmailVerification from "./EmailVerificationPopup";
import PhoneVerification from "./PhoneVerification";

interface EligibilityCriterion {
  id: string;
  label: string;
  status: "pending" | "verifying" | "verified" | "failed";
  failureReason?: string;
}

export default function EligibilityCriteriaPopUp({
  setEligibilityCriteriaPopUp,
  setApplicationRequirementPopUp,
  setIsOpen,
  selectedCourse,
  isAiCourseFinder = false,
}: {
  setEligibilityCriteriaPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  setApplicationRequirementPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCourse: Course | CourseItem;
  isAiCourseFinder?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { academicDetails, englishProficiencyDetails } = useAppSelector(
    (state) => state.course,
  );

  const [emailVerificationPopUp, setEmailVerificationPopup] =
    useState<boolean>(false);

  const [phoneVerificationPopUp, setPhoneVerificationPopUp] =
    useState<boolean>(false);

  const { isAnonymous, emailVerified, isVerified } = useAuth();
  const { userDetails } = useAppSelector((state) => state.userProfile);

  const [criteria, setCriteria] = useState<EligibilityCriterion[]>([
    { id: "pastQualification", label: "Past Qualification", status: "pending" },
    { id: "twelfthPercentage", label: "12th Percentage", status: "pending" },
    { id: "boardName", label: "Board Name", status: "pending" },
    { id: "english", label: "English Proficiency", status: "pending" },
    { id: "education_gap", label: "Education Gap", status: "pending" },
  ]);

  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  const [btnDisable, setBtnDisable] = useState(true);

  useEffect(() => {
    void sendCustomGaEvent("eligibility_criteria_popup_view", {
      course_id: selectedCourse?.course_id || "",
      is_ai_course_finder: isAiCourseFinder,
    });
  }, [isAiCourseFinder, selectedCourse?.course_id]);

  const buttonHandler = () => {
    void sendCustomGaEvent("eligibility_criteria_next_click", {
      course_id: selectedCourse?.course_id || "",
      is_anonymous: isAnonymous,
    });
    if (!isAnonymous && !isVerified) {
      setEmailVerificationPopup(true);
      return;
    }

    if (!isAnonymous && userDetails?.primary_verified === false) {
      setPhoneVerificationPopUp(true);
      return;
    }

    setEligibilityCriteriaPopUp(false);
    setApplicationRequirementPopUp(true);
    if (isAiCourseFinder) {
      dispatch(setApplicationRequirementsOpen(true));
    }
  };

  const checkEligibilityForCriterion = (
    id: string,
    selectedCourse: Course | CourseItem,
  ): { isEligible: boolean; reason?: string } => {
    const normalizeQualificationLevel = (value?: string) => {
      const normalized = (value || "")
        .toLowerCase()
        .trim()
        .replace(/['\u2019]s$/, "");

      if (normalized === "bachelors" || normalized === "undergraduate") {
        return "bachelor";
      }
      if (normalized === "masters" || normalized === "postgraduate") {
        return "master";
      }

      return normalized;
    };

    const qualificationRank: Record<string, number> = {
      "10th": 1,
      secondary: 1,
      ssc: 1,
      "12th": 2,
      "senior secondary": 2,
      hsc: 2,
      diploma: 3,
      bachelor: 4,
      ug: 4,
      master: 5,
      pg: 5,
      phd: 6,
      doctorate: 6,
    };

    const getQualificationRank = (value?: string) =>
      qualificationRank[normalizeQualificationLevel(value)] ?? 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isNullishOrMissing = (obj: any, keyPath: string[]): boolean => {
      let val = obj;
      for (const key of keyPath) {
        if (val == null || !(key in val)) return true;
        val = val[key];
      }
      return val == null;
    };

    switch (id) {
      case "pastQualification": {
        const courseLevel = normalizeQualificationLevel(
          selectedCourse.course_details?.course_level,
        );
        const pastQual = normalizeQualificationLevel(
          academicDetails.pastQualification,
        );

        if (pastQual === "12th") {
          const validLevels = ["bachelor", "diploma", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification (12th) is not sufficient for ${courseLevel} level courses. Valid course levels: Bachelor's, Diploma, or Certification.`,
            };
          }
        } else if (pastQual === "bachelor") {
          const validLevels = [
            "bachelor",
            "master",
            "diploma",
            "certification",
          ];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification is not sufficient for ${courseLevel} level courses. Valid course levels: Master's, Diploma, or Certification.`,
            };
          }
        } else if (pastQual === "master" || pastQual === "pg") {
          const validLevels = ["master", "phd", "doctorate", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your past qualification (Master's) cannot be used for ${courseLevel} level courses. To apply for a ${courseLevel} course, your past qualification should be 12th, Diploma, or Bachelor's.`,
            };
          }
        } else if (pastQual === "diploma") {
          const validLevels = ["bachelor", "diploma", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification (Diploma) is not sufficient for ${courseLevel} level courses. Valid course levels: Bachelor's, Diploma, or Certification.`,
            };
          }
        }

        if (
          isNullishOrMissing(selectedCourse, ["pre_requisite_details", "level"])
        ) {
          return { isEligible: true };
        }

        const requiredQualification =
          selectedCourse.pre_requisite_details?.level || "";
        const requiredRank = getQualificationRank(requiredQualification);
        const studentRank = getQualificationRank(
          academicDetails.pastQualification,
        );

        if (requiredRank > 0 && studentRank > 0 && studentRank < requiredRank) {
          return {
            isEligible: false,
            reason: `Required qualification level: ${requiredQualification}.`,
          };
        }

        return { isEligible: true };
      }

      case "twelfthPercentage": {
        if (isNullishOrMissing(selectedCourse, ["twelfth_percentage"])) {
          return { isEligible: true };
        }

        const studentPercentage = parseFloat(academicDetails.pastGrades);
        let requiredPercentage = selectedCourse.twelfth_percentage as any;
        if (typeof requiredPercentage === "object" && requiredPercentage !== null) {
          const boardName = englishProficiencyDetails?.selectBoard || "";
          const boardValue = requiredPercentage[boardName];
          if (boardValue !== undefined && boardValue !== null) {
            requiredPercentage = Number(boardValue);
          } else {
            const values = Object.values(requiredPercentage).map(Number).filter(v => !isNaN(v));
            requiredPercentage = values.length > 0 ? Math.min(...values) : 0;
          }
        } else {
          requiredPercentage = Number(requiredPercentage) || 0;
        }

        if (requiredPercentage > 0 && requiredPercentage <= 1) {
          requiredPercentage = requiredPercentage * 100;
        }

        if (studentPercentage < requiredPercentage) {
          return {
            isEligible: false,
            reason: `Your 12th percentage (${studentPercentage}%) is below the required ${requiredPercentage}%.`,
          };
        }

        return { isEligible: true };
      }

      case "boardName": {
        if (
          isNullishOrMissing(selectedCourse, ["state_board_not_accepted"]) ||
          !selectedCourse.state_board_not_accepted?.length
        ) {
          return { isEligible: true };
        }

        if (!englishProficiencyDetails.selectBoard) {
          return { isEligible: true };
        }

        const isNotAccepted = selectedCourse.state_board_not_accepted.includes(
          englishProficiencyDetails.selectBoard,
        );

        if (isNotAccepted) {
          return {
            isEligible: false,
            reason: `Your board (${englishProficiencyDetails.selectBoard}) is not accepted for this course.`,
          };
        }

        return { isEligible: true };
      }

      case "english": {
        if (
          isNullishOrMissing(selectedCourse, ["twelfth_english_percentage"]) &&
          isNullishOrMissing(selectedCourse, ["englishProficiencyRequirements"])
        ) {
          return { isEligible: true };
        }

        if (englishProficiencyDetails.test === "I-Don't-Have-Any") {
          if (!selectedCourse.english_waiver_accepted) {
            return {
              isEligible: false,
              reason: "English waiver is not accepted for this course.",
            };
          }

          if (selectedCourse.english_waiver_state_boards_accepted) {
            try {
              const acceptedBoards = JSON.parse(selectedCourse.english_waiver_state_boards_accepted);
              if (Array.isArray(acceptedBoards) && englishProficiencyDetails.selectBoard) {
                const selectedBoard = englishProficiencyDetails.selectBoard.toLowerCase();
                const isBoardAccepted = acceptedBoards.some(board => 
                  board.toLowerCase().includes(selectedBoard) ||
                  selectedBoard.includes(board.toLowerCase())
                );
                
                if (!isBoardAccepted) {
                  return {
                    isEligible: false,
                    reason: `Your board (${englishProficiencyDetails.selectBoard}) is not accepted for an English waiver for this course.`,
                  };
                }
              }
            } catch (e) {
              console.error("Failed to parse english_waiver_state_boards_accepted", e);
            }
          }

          const studentMarks = parseFloat(
            englishProficiencyDetails.englishMarks || "0",
          );
          let requiredMarks = selectedCourse.twelfth_english_percentage as any;
          if (typeof requiredMarks === "object" && requiredMarks !== null) {
            const boardName = englishProficiencyDetails?.selectBoard || "";
            const boardValue = requiredMarks[boardName];
            if (boardValue !== undefined && boardValue !== null) {
              requiredMarks = Number(boardValue);
            } else {
              const values = Object.values(requiredMarks).map(Number).filter(v => !isNaN(v));
              requiredMarks = values.length > 0 ? Math.min(...values) : 0;
            }
          } else {
            requiredMarks = Number(requiredMarks) || 0;
          }

          if (requiredMarks > 0 && requiredMarks <= 1) {
            requiredMarks = requiredMarks * 100;
          }

          if (
            !isNullishOrMissing(selectedCourse, [
              "twelfth_english_percentage",
            ]) &&
            studentMarks < requiredMarks
          ) {
            return {
              isEligible: false,
              reason: `Your 12th English marks (${studentMarks}%) are below the required ${requiredMarks}%.`,
            };
          }

          return { isEligible: true };
        } else {
          const requirement = englishProficiencyDetails.test
            ? selectedCourse.englishProficiencyRequirements?.[
                englishProficiencyDetails.test
              ]
            : undefined;

          if (!requirement) {
            return {
              isEligible: false,
              reason: `${englishProficiencyDetails.test} is not accepted for this course.`,
            };
          }

          const studentScores = {
            Overall: parseFloat(englishProficiencyDetails.overallScore || "0"),
            Reading: parseFloat(englishProficiencyDetails.readingScore || "0"),
            Writing: parseFloat(englishProficiencyDetails.writingScore || "0"),
            Speaking: parseFloat(
              englishProficiencyDetails.speakingScore || "0",
            ),
            Listening: parseFloat(
              englishProficiencyDetails.listeningScore || "0",
            ),
          };

          const failedSections = [];
          if (studentScores.Overall < (requirement.Overall ?? 0)) {
            failedSections.push(
              `Overall: ${studentScores.Overall} (required: ${requirement.Overall})`,
            );
          }
          if (studentScores.Reading > 0 && studentScores.Reading < (requirement.Reading ?? 0)) {
            failedSections.push(
              `Reading: ${studentScores.Reading} (required: ${requirement.Reading})`,
            );
          }
          if (studentScores.Writing > 0 && studentScores.Writing < (requirement.Writing ?? 0)) {
            failedSections.push(
              `Writing: ${studentScores.Writing} (required: ${requirement.Writing})`,
            );
          }
          if (studentScores.Speaking > 0 && studentScores.Speaking < (requirement.Speaking ?? 0)) {
            failedSections.push(
              `Speaking: ${studentScores.Speaking} (required: ${requirement.Speaking})`,
            );
          }
          if (studentScores.Listening > 0 && studentScores.Listening < (requirement.Listening ?? 0)) {
            failedSections.push(
              `Listening: ${studentScores.Listening} (required: ${requirement.Listening})`,
            );
          }

          if (failedSections.length > 0) {
            return {
              isEligible: false,
              reason: `${englishProficiencyDetails.test} scores below requirement: ${failedSections.join(", ")}.`,
            };
          }

          return { isEligible: true };
        }
      }

      case "education_gap": {
        if (!academicDetails.passingYear) {
          return { isEligible: true };
        }

        const currentYear = new Date().getFullYear();
        const passingYear = parseInt(academicDetails.passingYear);

        if (isNaN(passingYear) || passingYear > currentYear) {
          return {
            isEligible: false,
            reason: `Invalid passing year: ${academicDetails.passingYear}.`,
          };
        }

        const calculatedGap = currentYear - passingYear;
        const allowedGap = selectedCourse.gap_allowed ?? calculatedGap;

        if (calculatedGap > allowedGap) {
          return {
            isEligible: false,
            reason: `Your education gap (${calculatedGap} years) exceeds the maximum allowed gap of ${allowedGap} years.`,
          };
        }

        return { isEligible: true };
      }

      default:
        return { isEligible: true };
    }
  };

  const resetDetails = async () => {
    void sendCustomGaEvent("eligibility_criteria_edit_details_click", {
      course_id: selectedCourse?.course_id || "",
    });
    await dispatch(setCourseAcademicDetails({}));
    await dispatch(setCourseEnglishProficiencyDetails({}));
    setEligibilityCriteriaPopUp(false);
    setIsOpen(true);
    dispatch(setApplicationRequirementsOpen(false));
  };

  const runEligibilityCheck = async () => {
    setIsLoading(true);
    setStatusMessage({ type: "", text: "" });
    setBtnDisable(true);

    let foundFailed = false;

    for (let i = 0; i < criteria.length; i++) {
      const criterion = criteria[i];

      setCriteria((prev) =>
        prev.map((c, index) =>
          index === i ? { ...c, status: "verifying" } : c,
        ),
      );

      await new Promise((resolve) => setTimeout(resolve, 1200));

      const result = checkEligibilityForCriterion(criterion.id, selectedCourse);

      if (!result.isEligible) {
        foundFailed = true;

        // clear corresponding fields
        switch (criterion.id) {
          case "pastQualification":
          case "twelfthPercentage":
          case "education_gap":
            dispatch(setCourseAcademicDetails({}));
            break;
          case "boardName":
          case "english":
            dispatch(setCourseEnglishProficiencyDetails({}));
            break;
        }
      }

      setCriteria((prev) =>
        prev.map((c, index) =>
          index === i
            ? {
                ...c,
                status: result.isEligible ? "verified" : "failed",
                failureReason: result.reason,
              }
            : c,
        ),
      );

      if (foundFailed) {
        setBtnDisable(true);
      }
    }

    setStatusMessage({
      type: foundFailed ? "error" : "success",
      text: foundFailed
        ? isAiCourseFinder
          ? "Pre-requisite details not match"
          : "You are not eligible for this course."
        : "Congratulations! You are eligible for this course.",
    });

    setBtnDisable(foundFailed);
    setIsLoading(false);
  };

  useEffect(() => {
    runEligibilityCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case "verified":
        return <CheckCircle2 size={24} color="#16A34A" />;
      case "verifying":
        return <Loader2 size={24} color="#3B82F6" className="animate-spin" />;
      case "failed":
        return <XCircle size={24} color="#DC2626" />;
      default:
        return <Circle size={24} color="#9CA3AF" />;
    }
  };

  const renderCriteriaBlock = (
    title: string,
    items: EligibilityCriterion[],
    titleColor?: string,
  ) => (
    <div className="mb-6">
      <h3 className={`text-sm font-semibold mb-2 ff_poppins ${titleColor ?? "text-[#1C2233]"}`}>
        {title}
      </h3>
      <div className="space-y-4">
        {items.map((c) => (
          <div key={c.id} className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              {renderStatusIcon(c.status)}
              <span className="text-sm ff_poppins font-medium text-[#1C2233]">{c.label}</span>
            </div>
            {c.status === "failed" && c.failureReason && (
              <div className="ml-9 text-xs text-red-600 ff_poppins">
                {c.failureReason}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-[#E2E8F0] my-4"></div>
    </div>
  );

  const verified = criteria.filter((c) => c.status === "verified");
  const failed = criteria.filter((c) => c.status === "failed");
  const pending = criteria.filter(
    (c) => c.status !== "verified" && c.status !== "failed",
  );

  return (
    <>
      <div className="fixed bottom-0 left-0 w-full h-full z-[999] flex items-end justify-center overflow-hidden">
        <span
          className="inset-0 bg-black/50 backdrop-blur-sm absolute top-0 left-0 w-full h-full cursor-pointer"
          onClick={() => {
            setEligibilityCriteriaPopUp(false);
            dispatch(setApplicationRequirementsOpen(false));
          }}
        />
        <div className="bg-[#F8F9FF] text-[#1C2233] w-full max-w-md px-4 sm:px-6 py-6 rounded-t-3xl relative z-20 max-h-[90vh] overflow-y-auto shadow-2xl">
          <p className="text-[#3B82F6] text-[10px] sm:text-xs font-bold ff_poppins tracking-wider uppercase mb-1">
            Eligibility check
          </p>
          <h2 className="text-lg sm:text-xl ff_poppins font-bold text-[#1C2233] mb-4">
            Eligibility Criteria Check - Course wise
          </h2>

          <div className="border-t border-[#E2E8F0] my-4" />

          {verified.length > 0 &&
            renderCriteriaBlock("Criteria's Met", verified)}
          {failed.length > 0 &&
            renderCriteriaBlock("Failed Criteria", failed, "text-red-600")}
          {pending.length > 0 &&
            renderCriteriaBlock("Criteria's Pending", pending)}

          {statusMessage.text && (
            <div
              className="mb-4 p-3 rounded-md text-center border"
              style={
                statusMessage.type === "success"
                  ? { backgroundColor: "rgba(34,197,94,0.1)", borderColor: "rgba(22,163,74,0.4)", color: "#15803D" }
                  : { backgroundColor: "rgba(239,68,68,0.08)", borderColor: "rgba(220,38,38,0.4)", color: "#B91C1C" }
              }
            >
              {statusMessage.text}
            </div>
          )}

          <div className="ff_poppins bg-[#F0F6FF] text-[#1C2233] text-xs sm:text-sm font-medium flex flex-wrap justify-center items-center gap-1 p-3 rounded-2xl">
            Not your details?{" "}
            <span
              onClick={() => resetDetails()}
              className="flex items-center gap-2 text-nowrap text-xs sm:text-sm text-[#3B82F6] cursor-pointer"
            >
              <BlueEditPencil /> Edit details now
            </span>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <button
              className="cursor-pointer rounded-full w-full py-2.5 text-center font-semibold text-sm ff_poppins border border-[#3B82F6] text-[#3B82F6] hover:bg-[#F0F6FF]"
              onClick={() => {
                setEligibilityCriteriaPopUp(false);
                dispatch(setApplicationRequirementsOpen(false));
              }}
            >
              Cancel
            </button>
            <button
              onClick={buttonHandler}
              disabled={btnDisable || isLoading}
              type="submit"
              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-80 w-full font-semibold ff_poppins text-white text-sm md:text-base py-3 rounded-full gradient_button_shadow bg-button-gradient text-center"
            >
              {isLoading ? <WhiteSpinner /> : "Next"}
            </button>
          </div>
        </div>
      </div>

      {emailVerificationPopUp && (
        <div
          className="fixed bottom-0 left-0 w-full h-full z-[1000] flex items-end justify-center overflow-hidden"
          onClick={() => setEmailVerificationPopup(false)}
        >
          <span className="inset-0 bg-black/50 backdrop-blur-sm absolute top-0 left-0 w-full h-full cursor-pointer" />
          <div
            className="relative z-20 w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-[32px] bg-[#F8F9FF] shadow-2xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <EmailVerification
              setEmailVerificationPopup={setEmailVerificationPopup}
            />
          </div>
        </div>
      )}

      {phoneVerificationPopUp && (
        <PhoneVerification
          setPhoneVerificationPopup={setPhoneVerificationPopUp}
        />
      )}
    </>
  );
}
